import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { uploadFile, deleteFile, isAllowedFile, type UploadContext } from '@/services/fileService'
import type { FilePart } from '@/services/types'

export type FileStatus = 'pending' | 'uploading' | 'complete' | 'error'

export interface PendingFile {
  id: string
  file: File
  name: string
  size: number
  mimeType: string
  progress: number
  status: FileStatus
  fileId?: string
  error?: string
  uploadContext?: UploadContext
}

export const useFileUploadStore = defineStore('fileUpload', () => {
  const files = ref<PendingFile[]>([])

  const hasFiles = computed(() => files.value.length > 0)
  const isUploading = computed(() => files.value.some(f => f.status === 'uploading'))
  const allComplete = computed(() => files.value.every(f => f.status === 'complete'))

  function addFiles(newFiles: File[], uploadContext?: UploadContext): string[] {
    const added: string[] = []
    for (const file of newFiles) {
      if (!isAllowedFile(file)) continue
      const id = crypto.randomUUID()
      files.value.push({
        id,
        file,
        name: file.name,
        size: file.size,
        mimeType: file.type,
        progress: 0,
        status: 'pending',
        uploadContext,
      })
      added.push(id)
    }
    return added
  }

  async function upload(id: string): Promise<void> {
    const pf = files.value.find(f => f.id === id)
    if (!pf || pf.status === 'uploading' || pf.status === 'complete') return

    pf.status = 'uploading'
    pf.progress = 0
    pf.error = undefined

    try {
      const result = await uploadFile(pf.file, (pct) => {
        pf.progress = pct
      }, pf.uploadContext)
      pf.fileId = result.fileId
      pf.status = 'complete'
      pf.progress = 100
    } catch (err) {
      pf.status = 'error'
      pf.error = err instanceof Error ? err.message : 'Upload failed'
    }
  }

  async function uploadAll(): Promise<void> {
    const pending = files.value.filter(f => f.status === 'pending' || f.status === 'error')
    await Promise.all(pending.map(f => upload(f.id)))
  }

  async function remove(id: string): Promise<void> {
    const pf = files.value.find(f => f.id === id)
    if (!pf) return

    if (pf.fileId) {
      try {
        if (pf.uploadContext) {
          await deleteFile(pf.fileId, pf.uploadContext)
        }
      } catch {
        // Ignore delete errors
      }
    }
    files.value = files.value.filter(f => f.id !== id)
  }

  function clear(): void {
    files.value = []
  }

  function getCompletedFileParts(): FilePart[] {
    return files.value
      .filter(f => f.status === 'complete' && f.fileId)
      .map(f => ({
        type: 'file' as const,
        fileId: f.fileId!,
        name: f.name,
        mimeType: f.mimeType,
        size: f.size,
      }))
  }

  return {
    files,
    hasFiles,
    isUploading,
    allComplete,
    addFiles,
    upload,
    uploadAll,
    remove,
    clear,
    getCompletedFileParts,
  }
})
